import { useState, useMemo } from "react";
import {
  TrendingUp, Target, ChevronLeft, Sparkles, BarChart3,
  Calculator, Package, Briefcase, UtensilsCrossed,
} from "lucide-react";
import type { SavedProject } from "~/lib/burn-rate-store";
import { runProjection, runWhatIf } from "~/lib/burn-rate-projection";
import type { WhatIfResult } from "~/lib/burn-rate-projection";
import styles from "~/styles/burn-rate.module.css";

interface ProjectionViewProps {
  projects: SavedProject[];
  monthlyFixed: number;
  sym: string;
  fmt: (v: number) => string;
  onClose: () => void;
}

const MODE_ICONS = {
  services: Briefcase,
  recipes: UtensilsCrossed,
  product: Package,
};

export function ProjectionView({ projects, monthlyFixed, sym, fmt, onClose }: ProjectionViewProps) {
  const [goal, setGoal] = useState("");
  const [simPrice, setSimPrice] = useState("");
  const [simCost, setSimCost] = useState("");
  const [simUnits, setSimUnits] = useState("10");
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);

  const goalNum = parseFloat(goal) || 0;

  const projection = useMemo(() => {
    if (goalNum <= 0 || projects.length === 0) return null;
    return runProjection(projects, goalNum, monthlyFixed);
  }, [projects, goalNum, monthlyFixed]);

  const selectedProject = useMemo(() => {
    if (!projection || !selectedProjectId) return null;
    return projection.starProduct; // simplified: we show what-if for star product
  }, [projection, selectedProjectId]);

  const whatIf: WhatIfResult | null = useMemo(() => {
    if (!selectedProject || !simUnits) return null;
    const units = parseInt(simUnits, 10) || 0;
    const newP = parseFloat(simPrice) || selectedProject.netPrice;
    const newC = parseFloat(simCost) || selectedProject.baseCost;
    return runWhatIf({
      projectId: selectedProject.id,
      currentPrice: selectedProject.netPrice,
      currentCost: selectedProject.baseCost,
      newPrice: newP,
      newCost: newC,
      unitsSold: units,
    });
  }, [selectedProject, simPrice, simCost, simUnits]);

  const hasData = projects.length > 0;

  return (
    <div className={styles.projectionRoot}>
      {/* Header */}
      <div className={styles.projectionHeader}>
        <button className={styles.backBtn} onClick={onClose} aria-label="Volver">
          <ChevronLeft size={16} strokeWidth={1.5} />
        </button>
        <span className={styles.headerTag}>PROYECCIÓN</span>
        <div style={{ width: 36 }} />
      </div>

      {/* Goal Input */}
      <div className={styles.projectionHero}>
        <p className={styles.projectionTag}>META MENSUAL</p>
        <div className={styles.projectionGoalRow}>
          <span className={styles.projectionSym}>{sym}</span>
          <input
            className={styles.projectionGoalInput}
            type="number"
            inputMode="decimal"
            placeholder="0"
            value={goal}
            onChange={(e) => setGoal(e.target.value)}
          />
        </div>
        <p className={styles.projectionHint}>
          {hasData
            ? "Ingresa tu meta de ingreso mensual para generar escenarios."
            : "Guarda al menos un cálculo en la calculadora para ver proyecciones."}
        </p>
      </div>

      {!projection && hasData && (
        <div className={styles.projectionEmpty}>
          <Target size={32} strokeWidth={1.2} />
          <p>Escribe tu meta arriba para comenzar la simulación.</p>
        </div>
      )}

      {!hasData && (
        <div className={styles.projectionEmpty}>
          <BarChart3 size={32} strokeWidth={1.2} />
          <p>No tienes cálculos guardados todavía.</p>
          <p className={styles.projectionEmptySub}>Guarda proyectos en la pestaña Costos para activar las proyecciones.</p>
        </div>
      )}

      {projection && (
        <>
          {/* Star Product */}
          {projection.starProduct && (
            <div className={styles.projectionCard}>
              <div className={styles.projectionCardHead}>
                <Sparkles size={14} strokeWidth={1.5} />
                <span>PRODUCTO ESTRELLA</span>
              </div>
              <div className={styles.projectionStarBody}>
                <div className={styles.projectionStarIcon}>
                  {(() => {
                    const Icon = MODE_ICONS[projection.starProduct.mode] ?? Package;
                    return <Icon size={18} strokeWidth={1.4} />;
                  })()}
                </div>
                <div className={styles.projectionStarInfo}>
                  <p className={styles.projectionStarName}>{projection.starProduct.name}</p>
                  <p className={styles.projectionStarMeta}>
                    Guardado {projection.starProduct.frequency} {projection.starProduct.frequency === 1 ? "vez" : "veces"}
                    {" · "}
                    {sym}{fmt(projection.starProduct.netPrice)} / {projection.starProduct.mode === "services" ? "hora" : "unidad"}
                  </p>
                </div>
                <div className={styles.projectionStarProfit}>
                  <span className={styles.projectionStarProfitLabel}>Ganancia</span>
                  <span className={styles.projectionStarProfitValue}>
                    {sym}{fmt(projection.starProduct.profit)}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Break-even */}
          <div className={styles.projectionCard}>
            <div className={styles.projectionCardHead}>
              <TrendingUp size={14} strokeWidth={1.5} />
              <span>PUNTO DE EQUILIBRIO</span>
            </div>
            <div className={styles.projectionBeGrid}>
              {projection.breakEven.byProject.slice(0, 4).map((be) => (
                <div key={be.projectId} className={styles.projectionBeItem}>
                  <span className={styles.projectionBeName}>{be.name}</span>
                  <span className={styles.projectionBeValue}>
                    {be.unitsNeeded} {be.unitsNeeded === 1 ? "unidad" : "unidades"}
                  </span>
                  <span className={styles.projectionBeSub}>
                    {sym}{fmt(be.revenueAtUnits)} / mes
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Scenarios */}
          <div className={styles.projectionSectionTitle}>ESCENARIOS PARA ALCANZAR TU META</div>
          <div className={styles.projectionScenarios}>
            {projection.scenarios.map((sc) => (
              <div
                key={sc.id}
                className={`${styles.projectionScenario} ${sc.reachesGoal ? styles.projectionScenarioWin : ""}`}
              >
                <div className={styles.projectionScenarioHead}>
                  <span className={styles.projectionScenarioLabel}>{sc.label}</span>
                  {sc.reachesGoal && <span className={styles.projectionScenarioBadge}>ALCANZA META</span>}
                </div>
                <p className={styles.projectionScenarioDesc}>{sc.description}</p>
                <div className={styles.projectionScenarioItems}>
                  {sc.items.map((it, idx) => (
                    <div key={idx} className={styles.projectionScenarioItem}>
                      <span>{it.name}</span>
                      <span>{it.units} × {sym}{fmt(it.unitPrice)}</span>
                    </div>
                  ))}
                </div>
                <div className={styles.projectionScenarioFoot}>
                  <div>
                    <span className={styles.projectionScenarioFootLabel}>Ingreso</span>
                    <span className={styles.projectionScenarioFootValue}>{sym}{fmt(sc.totalRevenue)}</span>
                  </div>
                  <div>
                    <span className={styles.projectionScenarioFootLabel}>Ganancia</span>
                    <span className={styles.projectionScenarioFootValue}>{sym}{fmt(sc.totalProfit)}</span>
                  </div>
                  <div>
                    <span className={styles.projectionScenarioFootLabel}>vs Meta</span>
                    <span className={`${styles.projectionScenarioFootValue} ${sc.reachesGoal ? styles.projectionScenarioFootWin : styles.projectionScenarioFootLoss}`}>
                      {sc.reachesGoal ? `+${sym}${fmt(sc.totalRevenue - goalNum)}` : `-${sym}${fmt(sc.gapToGoal)}`}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* What-if Simulator */}
          {projection.starProduct && (
            <div className={styles.projectionCard}>
              <div className={styles.projectionCardHead}>
                <Calculator size={14} strokeWidth={1.5} />
                <span>SIMULADOR "¿Y SI…?"</span>
              </div>
              <p className={styles.projectionHint} style={{ marginBottom: 14 }}>
                Simula cambios de precio o costo para {projection.starProduct.name}.
              </p>
              <div className={styles.projectionSimGrid}>
                <label className={styles.projectionSimField}>
                  <span>Unidades vendidas</span>
                  <input
                    type="number"
                    min="1"
                    value={simUnits}
                    onChange={(e) => setSimUnits(e.target.value)}
                  />
                </label>
                <label className={styles.projectionSimField}>
                  <span>Nuevo precio ({sym})</span>
                  <input
                    type="number"
                    placeholder={fmt(projection.starProduct.netPrice)}
                    value={simPrice}
                    onChange={(e) => setSimPrice(e.target.value)}
                  />
                </label>
                <label className={styles.projectionSimField}>
                  <span>Nuevo costo ({sym})</span>
                  <input
                    type="number"
                    placeholder={fmt(projection.starProduct.baseCost)}
                    value={simCost}
                    onChange={(e) => setSimCost(e.target.value)}
                  />
                </label>
              </div>

              {whatIf && (
                <div className={styles.projectionSimResult}>
                  <div className={styles.projectionSimResultRow}>
                    <span>Ganancia actual</span>
                    <span>{sym}{fmt(whatIf.currentProfit)}</span>
                  </div>
                  <div className={styles.projectionSimResultRow}>
                    <span>Ganancia simulada</span>
                    <span className={whatIf.deltaProfit >= 0 ? styles.projectionSimUp : styles.projectionSimDown}>
                      {sym}{fmt(whatIf.newProfit)}
                      {" "}({whatIf.deltaProfit >= 0 ? "+" : ""}{sym}{fmt(whatIf.deltaProfit)})
                    </span>
                  </div>
                  <div className={styles.projectionSimResultRow}>
                    <span>Margen simulado</span>
                    <span>{whatIf.newMargin.toFixed(1)}%</span>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
